import React, { FC, useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  DELETE_ACCOUNT_CONFIRMATION,
  DeleteAccountRequest,
  DeleteAccountResponse,
} from '@dreamai/types';
import api from '../../api';
import DeleteAccountModal from './DeleteAccountModal';

/**
 * Rendered at the bottom of Settings → Your Account. The button only
 * opens the confirmation modal; the request is sent once the user has typed
 * the confirmation phrase. On success we end the Auth0 session — the backend
 * has already removed the user's rows and (normally) their Auth0 identity.
 */
const DeleteAccountSection: FC = () => {
  const { getAccessTokenSilently, logout } = useAuth0();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = useCallback(async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const body: DeleteAccountRequest = {
        confirmation: DELETE_ACCOUNT_CONFIRMATION,
      };
      await api.post<DeleteAccountResponse>('/accounts/delete', body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      logout({ logoutParams: { returnTo: window.location.origin } });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(
        axiosErr?.response?.data?.error ||
          'We could not delete your account. Please try again.',
      );
      setIsDeleting(false);
    }
  }, [isDeleting, getAccessTokenSilently, logout]);

  return (
    <>
      <div className='your-details-card delete-account-card'>
        <h3 className='delete-account-title'>Delete account</h3>
        <p className='delete-account-text'>
          Permanently delete your Dream account and everything we hold about
          you. This cannot be undone.
        </p>
        <button
          className='btn-delete-account'
          onClick={() => {
            setError(null);
            setIsModalOpen(true);
          }}
        >
          Delete my account
        </button>
      </div>

      <DeleteAccountModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
        isSubmitting={isDeleting}
        error={error}
      />
    </>
  );
};

export default DeleteAccountSection;
